import React, { useCallback } from 'react'

import { Button, Color, Spinner } from '@gousto-internal/citrus-react'
import actions from 'actions'
import { useDispatch } from 'react-redux'

import { AddRecipeButton as AddRecipeButtons } from '@library/recipe-customisations'

import { useIsAdminMenuPreview, useIsRecipeInStock } from 'hooks/menu-recipes/useIsRecipeInStock'
import type { RecipeUUID } from 'reducers/menu-recipes/recipes'
import { useBasket } from 'routes/Menu/domains/basket'
import { formatSurcharge } from 'routes/Menu/utils/formatSurcharge'

import css from './AddRecipeButton.css'

type AddRecipeButtonProps = {
  id: RecipeUUID
  position?: string
  surcharge?: number
  surchargeLabel?: string
  isNegativeSurcharge?: boolean
  isRecipeAvailable?: boolean
  loading?: boolean
  onRecipeDetailsClose?: () => void
}

export const AddRecipeButton = ({
  id,
  position = '',
  surcharge,
  surchargeLabel,
  isNegativeSurcharge,
  isRecipeAvailable,
  loading = false,
  onRecipeDetailsClose,
}: AddRecipeButtonProps) => {
  const dispatch = useDispatch()
  const isAdminMenuPreview = useIsAdminMenuPreview()
  const isRecipeInStock = useIsRecipeInStock(id)
  const {
    reachedLimit,
    getQuantitiesForRecipeId,
    canAddRecipes,
    addRecipeByUuid,
    removeRecipeByUuid,
  } = useBasket()
  const qty = getQuantitiesForRecipeId(id)
  const isInBasket = qty > 0
  const isOutOfStock = !(isRecipeAvailable ?? isRecipeInStock) && !isInBasket && !isAdminMenuPreview

  // TODO: Clean up this logic once fully migrated to the menu experience API
  const resolvedSurchargeLabel =
    surchargeLabel ?? (surcharge ? `${formatSurcharge(surcharge)} per portion` : '')
  const resolvedNegativeSurcharge = isNegativeSurcharge ?? Boolean(surcharge && surcharge < 0)

  const closeRecipeDetails = useCallback(() => {
    onRecipeDetailsClose?.()
  }, [onRecipeDetailsClose])

  const handleAdd = useCallback(
    (isFirstInBatchOfSameRecipes: boolean) => {
      if (!isOutOfStock && canAddRecipes) {
        addRecipeByUuid(id, 'view_recipe_detail_screen', { position })

        if (isFirstInBatchOfSameRecipes) {
          closeRecipeDetails()
        }
      } else {
        closeRecipeDetails()
        setTimeout(() => {
          dispatch(actions.menuBrowseCTAVisibilityChange(true))
        }, 500)
      }
    },
    [isOutOfStock, canAddRecipes, addRecipeByUuid, id, position, closeRecipeDetails, dispatch],
  )

  const handleRemove = useCallback(() => {
    removeRecipeByUuid(id, 'detail', position)
  }, [id, position, removeRecipeByUuid])

  return (
    <div className={css.addRecipeWrapper}>
      {loading ? (
        <Button aria-busy aria-label="Loading recipe availability" disabled width="100%">
          <Spinner color={Color.White} size={6} />
        </Button>
      ) : (
        <AddRecipeButtons
          canAddRecipes={canAddRecipes}
          isNegativeSurcharge={resolvedNegativeSurcharge}
          isOutOfStock={isOutOfStock}
          onClickAdd={handleAdd}
          onClickRemove={handleRemove}
          quantity={qty}
          reachedLimit={reachedLimit}
          surchargeLabel={resolvedSurchargeLabel}
        />
      )}
    </div>
  )
}
